<template>
  <v-container fluid>
    <!-- Page Header -->
    <v-row class="mb-4" align="center">
      <v-col>
        <div class="d-flex align-center">
          <v-avatar color="primary" size="52" class="mr-3 elevation-2">
            <v-icon color="white" size="28">mdi-scale-balance</v-icon>
          </v-avatar>
          <div>
            <div class="text-h5 font-weight-bold grey--text text--darken-2">Balanza de Comprobación</div>
            <div class="text-caption grey--text">Saldo de cuentas contables y movimiento en el período seleccionado</div>
          </div>
        </div>
      </v-col>

      <!-- Action bar -->
      <v-col cols="auto" class="d-flex align-center no-print">
        <v-autocomplete
          v-if="currencies.length > 1"
          :items="currencies"
          v-model="currency_show"
          outlined dense hide-details
          item-text="code" return-object
          label="Moneda" item-value="id"
          style="max-width: 130px"
          class="mr-3"
        ></v-autocomplete>

        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn icon color="grey darken-1" v-bind="attrs" v-on="on"
              @click="print_trial()" :disabled="catalog_filtered.length === 0" class="mr-1">
              <v-icon>print</v-icon>
            </v-btn>
          </template>
          <span>Imprimir</span>
        </v-tooltip>

        <v-btn color="success" outlined class="mr-2 text-none"
          @click="downloadXLSX" :disabled="catalog_filtered.length === 0">
          <v-icon left small>mdi-file-excel-outline</v-icon>
          Excel
        </v-btn>

        <v-btn color="primary" outlined class="text-none"
          @click="show_filters = true">
          <v-icon left small>filter_alt</v-icon>
          Filtros
        </v-btn>
      </v-col>
    </v-row>

    <!-- Active filter summary -->
    <v-card v-if="filter_apply.business_unit" outlined class="rounded-xl mb-4 pa-3 no-print">
      <v-row align="center" dense>
        <v-col cols="auto">
          <v-icon color="primary" small class="mr-1">mdi-information-outline</v-icon>
          <span class="text-caption font-weight-bold grey--text text-uppercase">Filtros activos</span>
        </v-col>
        <v-col cols="auto">
          <v-chip small outlined color="primary">
            <v-icon left x-small>mdi-office-building</v-icon>
            {{ filter_apply.location?.description_long || filter_apply.business_unit?.description_long }}
          </v-chip>
        </v-col>
        <v-col cols="auto">
          <v-chip small outlined color="grey darken-1">
            <v-icon left x-small>mdi-calendar-range</v-icon>
            {{ formatDate(filter_apply.dateStart) }} — {{ formatDate(filter_apply.dateEnd) }}
          </v-chip>
        </v-col>
        <v-col cols="auto" v-if="currency_show.code">
          <v-chip small outlined color="indigo">
            <v-icon left x-small>mdi-currency-usd</v-icon>
            {{ currency_show.code }}
          </v-chip>
        </v-col>
      </v-row>
    </v-card>

    <!-- Filter dialog -->
    <HeaderFiltersReports
      :open_dialog="show_filters"
      @set_filters="set_filters"
      :loading="loading_filter"
      :filter_options="{
          show_calendar: true,
          show_supplier_type: false,
          show_customer_type: false,
          show_date_type: false,
          show_date_range: true,
          show_language_type: false,
          entity_type: 2
        }"
    />

    <!-- Main content card -->
    <v-card outlined class="rounded-xl">
      <!-- Column header toolbar -->
      <div class="grey lighten-5 rounded-t-xl px-4 py-3 border-bottom no-print">
        <div class="d-flex align-center w-100">
          <span class="text-caption font-weight-bold grey--text text--darken-2 text-uppercase">Cuenta</span>
          
          <div class="d-flex align-center ml-auto">
            <!-- Header Balance Inicial -->
            <div style="width: 220px" class="d-flex flex-column align-center">
              <span class="text-overline font-weight-black grey--text text-uppercase" style="line-height: 1.2">Balance Inicial</span>
              <div class="d-flex w-100 px-2 mt-1">
                <span class="text-caption font-weight-bold primary--text text-uppercase text-right flex-grow-1">Deb</span>
                <div style="width:1px; height:10px; background:#e0e0e0" class="mx-2 mt-1"></div>
                <span class="text-caption font-weight-bold error--text text-uppercase text-right flex-grow-1">Cre</span>
              </div>
            </div>

            <!-- Header Movimiento -->
            <div style="width: 220px" class="d-flex flex-column align-center mx-1">
              <span class="text-overline font-weight-black grey--text text-uppercase" style="line-height: 1.2">Movimiento</span>
              <div class="d-flex w-100 px-2 mt-1">
                <span class="text-caption font-weight-bold primary--text text-uppercase text-right flex-grow-1">Deb</span>
                <div style="width:1px; height:10px; background:#e0e0e0" class="mx-2 mt-1"></div>
                <span class="text-caption font-weight-bold error--text text-uppercase text-right flex-grow-1">Cre</span>
              </div>
            </div>

            <!-- Header Balance Final -->
            <div style="width: 220px" class="d-flex flex-column align-center">
              <span class="text-overline font-weight-black grey--text text-uppercase" style="line-height: 1.2">Balance Final</span>
              <div class="d-flex w-100 px-2 mt-1">
                <span class="text-caption font-weight-bold primary--text text-uppercase text-right flex-grow-1">Deb</span>
                <div style="width:1px; height:10px; background:#e0e0e0" class="mx-2 mt-1"></div>
                <span class="text-caption font-weight-bold error--text text-uppercase text-right flex-grow-1">Cre</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <v-divider></v-divider>

      <!-- Loading -->
      <div v-if="loading_data" class="text-center py-12">
        <v-progress-circular indeterminate color="primary" size="48"></v-progress-circular>
        <div class="text-body-2 grey--text mt-3">Cargando balanza...</div>
      </div>

      <!-- Empty state -->
      <div v-else-if="catalog_filtered.length === 0" class="text-center py-12">
        <v-icon size="72" color="grey lighten-1">mdi-scale-balance</v-icon>
        <div class="text-h6 grey--text mt-3">Sin datos para mostrar</div>
        <div class="text-body-2 grey--text mt-1">Aplica los filtros para consultar la balanza de comprobación</div>
        <v-btn color="primary" outlined class="mt-4 text-none no-print" @click="show_filters = true">
          <v-icon left small>filter_alt</v-icon>
          Aplicar filtros
        </v-btn>
      </div>

      <!-- Treeview -->
      <v-treeview
        v-else
        hoverable
        return-object
        class="mt-0 trial-tree"
        :activatable="false"
        :items="catalog_filtered"
        item-key="chart_of_account_code"
        :open.sync="expandedNodes"
      >
        <template v-slot:prepend="{ item }">
          <v-icon small :color="item.children && item.children.length > 0 ? 'primary' : 'grey'">
            {{ item.children && item.children.length > 0 ? 'mdi-folder-outline' : 'mdi-circle-small' }}
          </v-icon>
        </template>

        <template slot="label" slot-scope="{ item }">
          <span class="primary--text font-weight-bold trial-link" @click="ViewAccount(item)">
            {{ item.chart_of_account_code }}
          </span>
          <span class="ml-1 grey--text text--darken-2">{{ item.chart_of_account_name }}</span>
        </template>

        <template slot="append" slot-scope="{ item }">
          <div class="d-flex align-center ml-auto">
            <!-- Balance Inicial -->
            <div class="d-flex align-center px-2" style="width: 220px">
              <span class="text-body-2 font-weight-medium text-right flex-grow-1"
                :class="(parseFloat(item.initial_debit_amount) - parseFloat(item.initial_credit_amount)) > 0 ? 'primary--text' : 'grey--text text--lighten-2'">
                {{ (parseFloat(item.initial_debit_amount) - parseFloat(item.initial_credit_amount)) > 0 ? currencyFormat(parseFloat(item.initial_debit_amount) - parseFloat(item.initial_credit_amount)) : '—' }}
              </span>
              <div style="width:1px; height:16px; background:#f5f5f5" class="mx-2"></div>
              <span class="text-body-2 font-weight-medium text-right flex-grow-1"
                :class="(parseFloat(item.initial_debit_amount) - parseFloat(item.initial_credit_amount)) < 0 ? 'error--text' : 'grey--text text--lighten-2'">
                {{ (parseFloat(item.initial_debit_amount) - parseFloat(item.initial_credit_amount)) < 0 ? currencyFormat(Math.abs(parseFloat(item.initial_debit_amount) - parseFloat(item.initial_credit_amount))) : '—' }}
              </span>
            </div>

            <!-- Movimiento del Periodo -->
            <div class="d-flex align-center px-2 mx-1" style="width: 220px">
              <span class="text-body-2 font-weight-medium text-right flex-grow-1"
                :class="(parseFloat(item.debit_amount) - parseFloat(item.credit_amount)) > 0 ? 'primary--text' : 'grey--text text--lighten-2'">
                {{ (parseFloat(item.debit_amount) - parseFloat(item.credit_amount)) > 0 ? currencyFormat(parseFloat(item.debit_amount) - parseFloat(item.credit_amount)) : '—' }}
              </span>
              <div style="width:1px; height:16px; background:#f5f5f5" class="mx-2"></div>
              <span class="text-body-2 font-weight-medium text-right flex-grow-1"
                :class="(parseFloat(item.debit_amount) - parseFloat(item.credit_amount)) < 0 ? 'error--text' : 'grey--text text--lighten-2'">
                {{ (parseFloat(item.debit_amount) - parseFloat(item.credit_amount)) < 0 ? currencyFormat(Math.abs(parseFloat(item.debit_amount) - parseFloat(item.credit_amount))) : '—' }}
              </span>
            </div>

            <!-- Balance Final -->
            <div class="d-flex align-center px-2" style="width: 220px">
              <span class="text-body-2 font-weight-bold text-right flex-grow-1"
                :class="((parseFloat(item.initial_debit_amount) - parseFloat(item.initial_credit_amount)) + (parseFloat(item.debit_amount) - parseFloat(item.credit_amount))) > 0 ? 'indigo--text text--darken-2' : 'grey--text text--lighten-2'">
                {{ ((parseFloat(item.initial_debit_amount) - parseFloat(item.initial_credit_amount)) + (parseFloat(item.debit_amount) - parseFloat(item.credit_amount))) > 0 ? currencyFormat((parseFloat(item.initial_debit_amount) - parseFloat(item.initial_credit_amount)) + (parseFloat(item.debit_amount) - parseFloat(item.credit_amount))) : '—' }}
              </span>
              <div style="width:1px; height:16px; background:#f5f5f5" class="mx-2"></div>
              <span class="text-body-2 font-weight-bold text-right flex-grow-1"
                :class="((parseFloat(item.initial_debit_amount) - parseFloat(item.initial_credit_amount)) + (parseFloat(item.debit_amount) - parseFloat(item.credit_amount))) < 0 ? 'error--text text--darken-2' : 'grey--text text--lighten-2'">
                {{ ((parseFloat(item.initial_debit_amount) - parseFloat(item.initial_credit_amount)) + (parseFloat(item.debit_amount) - parseFloat(item.credit_amount))) < 0 ? currencyFormat(Math.abs((parseFloat(item.initial_debit_amount) - parseFloat(item.initial_credit_amount)) + (parseFloat(item.debit_amount) - parseFloat(item.credit_amount)))) : '—' }}
              </span>
            </div>
          </div>
        </template>
      </v-treeview>

      <!-- Totals row -->
      <template v-if="catalog_filtered.length > 0 && !loading_data">
        <v-divider></v-divider>
        <v-card flat color="grey lighten-4" class="rounded-b-xl">
          <v-card-text class="py-3 px-4">
            <div class="d-flex align-center">
              <span class="font-weight-black text-subtitle-2 grey--text text--darken-3">TOTALES</span>
              
              <div class="d-flex align-center ml-auto">
                <!-- Totals for Initial -->
                <div class="d-flex align-center px-2" style="width: 220px">
                  <span class="font-weight-black primary--text text-right flex-grow-1">
                    {{ currencyFormat(grand_totals.initial_debit_amount) }}
                  </span>
                  <div style="width:1px; height:20px; background:#bdbdbd" class="mx-2"></div>
                  <span class="font-weight-black error--text text-right flex-grow-1">
                    {{ currencyFormat(grand_totals.initial_credit_amount) }}
                  </span>
                </div>

                <!-- Totals for Movement -->
                <div class="d-flex align-center px-2 mx-1" style="width: 220px">
                  <span class="font-weight-black primary--text text-right flex-grow-1">
                    {{ currencyFormat(grand_totals.debit_amount) }}
                  </span>
                  <div style="width:1px; height:20px; background:#bdbdbd" class="mx-2"></div>
                  <span class="font-weight-black error--text text-right flex-grow-1">
                    {{ currencyFormat(grand_totals.credit_amount) }}
                  </span>
                </div>

                <!-- Totals for Final -->
                <div class="d-flex align-center px-2" style="width: 220px">
                  <span class="font-weight-black indigo--text text--darken-4 text-right flex-grow-1">
                    {{ currencyFormat(grand_totals.final_debit) }}
                  </span>
                  <div style="width:1px; height:20px; background:#bdbdbd" class="mx-2"></div>
                  <span class="font-weight-black error--text text--darken-4 text-right flex-grow-1">
                    {{ currencyFormat(grand_totals.final_credit) }}
                  </span>
                </div>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </template>
    </v-card>
  </v-container>
</template>

<script>
import moment from "moment";
import ExcelJS from "exceljs";
import jsPDF from "jspdf";
import HeaderFiltersReports from "@/components/Accounting/HeaderFiltersReports.vue";
export default {
  data: () => ({
    currency_show:{
        id:null,
        code:null
    },
    expandedNodes:[],
    show_filters: true,
    loading_filter: false,
    loading_data: false,
    catalog_x_currency: [],
    accounts: [],
    currencies:[],
    filter_apply: {},
  }),
  mounted() {
    this.$root.$emit("toggle-mini", true);
  },
  components: {
    HeaderFiltersReports,
  },
  computed: {
    catalog_filtered(){
        if(this.catalog_x_currency.length== 0){
            return []
        }
        for (let obj of this.catalog_x_currency) {
                let keys = Object.keys(obj);
                for (let key of keys) {
                    if(key==this.currency_show.code){
                        return obj[key]
                    }
                }
            }
    },
    title(){
        if(this.filter_apply.location?.id!=null){
            return this.filter_apply.location?.description_long
        }
        return this.filter_apply.business_unit?.description_long
    },
    grand_totals() {
      let totals = {
        initial_debit_amount: 0,
        initial_credit_amount: 0,
        debit_amount: 0,
        credit_amount: 0,
        final_debit: 0,
        final_credit: 0
      };
      
      if (!this.catalog_filtered) return totals;

      for (let item of this.catalog_filtered) {
        let balance_inicial = parseFloat(item.initial_debit_amount) - parseFloat(item.initial_credit_amount);
        let balance_periodo = parseFloat(item.debit_amount) - parseFloat(item.credit_amount);
        let final_balance = balance_inicial + balance_periodo;

        totals.initial_debit_amount += balance_inicial > 0 ? balance_inicial : 0;
        totals.initial_credit_amount += balance_inicial < 0 ? (balance_inicial * -1) : 0;
        
        totals.debit_amount += balance_periodo > 0 ? balance_periodo : 0;
        totals.credit_amount += balance_periodo < 0 ? (balance_periodo * -1) : 0;
        
        totals.final_debit += final_balance > 0 ? final_balance : 0;
        totals.final_credit += final_balance < 0 ? (final_balance * -1) : 0;
      }
      return totals;
    },
    print_title(){
      return `${this.firtsLetterMinRestUp(this.title)} desde ${moment(this.filter_apply.dateStart).format('DD/MM/YYYY')} hasta ${moment(this.filter_apply.dateEnd).format('DD/MM/YYYY')}`;
    }
  },
  methods: {
    async getData(filters) {
        this.loading_data = true;
        this.filter_apply=filters;
        this.currencies = [];
        this.catalog_x_currency=[];

        const resultCurrencies = await this.api_Post("accounting/currenciesBusiness",
              {
                business: filters.business_unit.id,
                effective_date: filters.dateEnd,
              }
        );          
            const all_currencys = await resultCurrencies.json();
            this.currencies= Object.assign([],all_currencys)
            let include_currency_id=false,currency_to=null;
            //SI ES A MONEDA BASE HACE UNA SOLA CONSULTA CON TODAS LAS MONEDAS CONVIRTIENDOLAS A LA BASE
            if(filters.currency_type==1){
                this.currencies=this.currencies.filter((element)=>element.id==element.default_currency_id);
            }
            //SI ES A UNA MONEDA ESPECIFICA SOLO BUSCA ESA MONEDA
            if(filters.currency_type==2 && filters.currency.id!=0){
                this.currencies= this.currencies.filter((element)=>element.id==filters.currency.id);
                include_currency_id=true;
            }
            if(filters.currency_type==2 && filters.currency.id==0){
                include_currency_id=true;
            }
            //SI ES CONVETIR TODO A MONEDA X
            if(filters.currency_type==3){
                this.currencies=this.currencies.filter((element)=>element.id==filters.currency.id);
                currency_to=filters.currency.id;
            }

      for (let currency of this.currencies) { 
        const resultTypeAccounts = await this.api_Post("accounting/trial_balance", {
            location_id: filters.location?filters.location.id:this.$store.state.auth.user.acess_locations.toString(),
            business_units: filters.business_unit.id,
            include_currency_id: include_currency_id,
            currency_id: currency.id,
            start_date: filters.dateStart,
            end_date: filters.dateEnd,
            initial_start_date: filters.calendar.date_from,
            currencies:all_currencys,
            convert_to:currency_to,
            currency_type:filters.currency_type,

        });
        let dataTypeAccounts = await resultTypeAccounts.json();

        this.catalog_x_currency.push({[currency.code]:this.build_tree(dataTypeAccounts)});
        this.currency_show=this.currencies[0];
      }
      this.loading_data = false;
    },
    build_tree(dataTypeAccounts) {
        let child = [];
        for (let items of dataTypeAccounts) {
          child.push({
            chart_of_account_id: items.chart_of_account_id,
            chart_of_account_name: items.chart_of_account_name,
            parent: items.parent_id,
            chart_of_account_code: items.chart_of_account_code,
            subcategory_id: items.subcategory_id,
            subcategory_name: items.subcategory_name,
            initial_debit_amount: parseFloat(items.initial_debit_amount),
            initial_credit_amount: parseFloat(items.initial_credit_amount),
            debit_amount: parseFloat(items.debit_amount),
            credit_amount: parseFloat(items.credit_amount),
          });
        }

        child.sort((a, b) =>
          a.chart_of_account_code.localeCompare(
            b.chart_of_account_code,
            undefined,
            { numeric: true }
          )
        );

        // Crear un mapa para agrupar subcategorías bajo su cuenta principal
        let accountMap = new Map();

        for (let item of child) {
          if (item.subcategory_id) {
            let parentCode = item.chart_of_account_code;
            if (!accountMap.has(parentCode)) {
              accountMap.set(parentCode, {
                chart_of_account_id: item.chart_of_account_id,
                chart_of_account_name: item.chart_of_account_name,
                parent: item.parent,
                chart_of_account_code: item.chart_of_account_code,
                initial_debit_amount: 0,
                initial_credit_amount: 0,
                debit_amount: 0,
                credit_amount: 0,
                children: [],
              });
            }
            
            // Buscar si la subcategoría ya existe en los children
            let existingSubcategory = accountMap
              .get(parentCode)
              .children.find((child) => child.chart_of_account_id === item.subcategory_id);

            if (existingSubcategory) {
              // Sumar montos si la subcategoría ya existe
              existingSubcategory.initial_debit_amount += parseFloat(item.initial_debit_amount);
              existingSubcategory.initial_credit_amount += parseFloat(item.initial_credit_amount);
              existingSubcategory.debit_amount += parseFloat(item.debit_amount);
              existingSubcategory.credit_amount += parseFloat(item.credit_amount);
            } else {
              // Si no existe, agregar la subcategoría como un nuevo hijo
              accountMap.get(parentCode).children.push({
                chart_of_account_id: item.subcategory_id,
                chart_of_account_name: item.subcategory_name,
                parent: item.chart_of_account_id,
                chart_of_account_code: item.chart_of_account_code,
                initial_debit_amount: parseFloat(item.initial_debit_amount),
                initial_credit_amount: parseFloat(item.initial_credit_amount),
                debit_amount: parseFloat(item.debit_amount),
                credit_amount: parseFloat(item.credit_amount),
              });
            }
          } else {
            if (!accountMap.has(item.chart_of_account_code)) {
              accountMap.set(item.chart_of_account_code, {
                chart_of_account_id: item.chart_of_account_id,
                chart_of_account_name: item.chart_of_account_name,
                parent: item.parent,
                chart_of_account_code: item.chart_of_account_code,
                initial_debit_amount: parseFloat(item.initial_debit_amount),
                initial_credit_amount: parseFloat(item.initial_credit_amount),
                debit_amount: parseFloat(item.debit_amount),
                credit_amount: parseFloat(item.credit_amount),
                children: [],
              });
            } else {
              // Sumar los montos de la cuenta principal si ya existe
              accountMap.get(item.chart_of_account_code).initial_debit_amount += parseFloat(item.initial_debit_amount);
              accountMap.get(item.chart_of_account_code).initial_credit_amount += parseFloat(item.initial_credit_amount);
              accountMap.get(item.chart_of_account_code).debit_amount += parseFloat(item.debit_amount);
              accountMap.get(item.chart_of_account_code).credit_amount += parseFloat(item.credit_amount);
            }
          }
        }

        // Sumar los montos de las subcategorías a sus cuentas principales
        accountMap.forEach((account) => {
          account.children.forEach((sub) => {
            account.debit_amount += sub.debit_amount;
            account.credit_amount += sub.credit_amount;
            account.initial_debit_amount += sub.initial_debit_amount;
            account.initial_credit_amount += sub.initial_credit_amount;
          });
        });

        // Convertir el mapa a una lista para mantener el formato esperado
        let result = Array.from(accountMap.values());

        // Volver a anidar las cuentas en el árbol
        const nest = (items, chart_of_account_id = null, link = "parent") =>
          items
            .filter((item) => item[link] === chart_of_account_id)
            .map((item) => {
              let children = nest(items, item.chart_of_account_id);

              // Sumar los montos de los hijos al padre
              let totalInitialDebit = children.reduce((sum, child) => sum + parseFloat(child.initial_debit_amount), 0);
              let totalInitialCredit = children.reduce((sum, child) => sum + parseFloat(child.initial_credit_amount), 0);
              let totalDebit = children.reduce((sum, child) => sum + parseFloat(child.debit_amount), 0);
              let totalCredit = children.reduce((sum, child) => sum + parseFloat(child.credit_amount), 0);

              return {
                ...item,
                initial_debit_amount: (parseFloat(item.initial_debit_amount) + totalInitialDebit).toFixed(2),
                initial_credit_amount: (parseFloat(item.initial_credit_amount) + totalInitialCredit).toFixed(2),
                debit_amount: (parseFloat(item.debit_amount) + totalDebit).toFixed(2),
                credit_amount: (parseFloat(item.credit_amount) + totalCredit).toFixed(2),
                children: [...item.children, ...children],
              };
            });

        let finalResult = nest(result);

        function removeZeroAmounts(dataArray) {
          let newArray = [];
          for (let data of dataArray) {
            if (parseFloat(data.initial_debit_amount) === 0.0 && parseFloat(data.initial_credit_amount) === 0.0 && parseFloat(data.debit_amount) === 0.0 && parseFloat(data.credit_amount) === 0.0) {
              continue;
            }

            let newChildren = [];
            if (Array.isArray(data.children)) {
              for (let child of data.children) {
                let newChild = removeZeroAmounts([child]);
                if (newChild.length > 0) {
                  newChildren.push(newChild[0]);
                }
              }
            }

            data.children = newChildren;
            newArray.push(data);
          }
          return newArray;
        }
        
        const tree = removeZeroAmounts(finalResult);
        return tree;
      },
    flattenTree(data, level = 0, result = []) {
      data.forEach((node) => {
        result.push({
          id: node.chart_of_account_id,
          chart_of_account_name: node.chart_of_account_name,
          parent: node.parent,
          chart_of_account_code: node.chart_of_account_code,
          initial_debit_amount: node.initial_debit_amount,
          initial_credit_amount: node.initial_credit_amount,
          debit_amount: node.debit_amount,
          credit_amount: node.credit_amount,
          hasChildren: node.children && node.children.length > 0,
          level: level,
        });
        if (node.children && node.children.length > 0) {
          this.flattenTree(node.children, level + 1, result);
        }
      });
      return result;
    },
    async downloadXLSX() {
        const workbook = new ExcelJS.Workbook();
        for (let obj of this.catalog_x_currency) {
                let keys = Object.keys(obj);
                for (let key of keys) {
                    const flattenedData = this.flattenTree(obj[key]);                   
                    const worksheet = workbook.addWorksheet(key);
                    

                    worksheet.columns = [
                    { header: "Codigo cuenta", key: "chart_of_account_code", width: 15 },
                    { header: "Nombre cuenta", key: "chart_of_account_name", width: 80 },
                    { header: "Débito", key: "initial_debit_amount", width: 15 },
                    { header: "Crédito", key: "initial_credit_amount", width: 15 },
                    { header: "Débito", key: "debit_amount", width: 15 },
                    { header: "Crédito", key: "credit_amount", width: 15 },
                    { header: "Débito", key: "final_debit", width: 15 },
                    { header: "Crédito", key: "final_credit", width: 15 },
                    ];

                    const titleRow = worksheet.insertRow(1, [`BALANZA DE COMPROBACIÓN ${this.title} DESDE ${moment(this.filter_apply.dateStart).format("DD/MM/YYYY")} HASTA ${moment(this.filter_apply.dateEnd).format("DD/MM/YYYY")}`]);
                    worksheet.mergeCells('A1:H1'); // Merge cells A1 to H1 (adjust based on the number of columns)
                    titleRow.getCell(1).font = { size: 14, bold: true }; // Apply style to the title
                    titleRow.getCell(1).alignment = { horizontal: 'center' }; // Center the title

                    const currency = worksheet.insertRow(2, [`Valor expresado en (${key})`]);
                    worksheet.mergeCells('A2:H2');
                    currency.getCell(1).font = { size: 14, bold: true }; // Apply style to the title
                    currency.getCell(1).alignment = { horizontal: 'center' }; // Center the title

                    const subtitleRow = worksheet.insertRow(3, ['', '', 'Balance inicial', '', 'Movimiento del periodo', '', 'Balance final', '']);
                    worksheet.mergeCells('C3:D3');
                    worksheet.mergeCells('E3:F3');
                    worksheet.mergeCells('G3:H3');
                    subtitleRow.getCell(3).alignment = { horizontal: 'center' };
                    subtitleRow.getCell(5).alignment = { horizontal: 'center' };
                    subtitleRow.getCell(7).alignment = { horizontal: 'center' };
                    
                    const accountingFormat = '_(* #,##0.00_);_(* \\(#,##0.00\\);_(* "-"??_);_(@_)';
                    flattenedData.forEach((row) => {
                    const indent = " ".repeat(row.level * 4);
                    
                     let balance_inicial_a =  parseFloat(row.initial_debit_amount) - parseFloat(row.initial_credit_amount);
                      let balance_perido_a =  parseFloat(row.debit_amount)-parseFloat(row.credit_amount);
                      let final_balance_a = balance_inicial_a + balance_perido_a;

                  
                    const newRow = worksheet.addRow({
                        chart_of_account_code: row.chart_of_account_code,
                        chart_of_account_name: indent + row.chart_of_account_name,
                        initial_debit_amount: balance_inicial_a>0?balance_inicial_a:0,
                        initial_credit_amount: balance_inicial_a<0?(balance_inicial_a*-1):0,
                        debit_amount: balance_perido_a>0?balance_perido_a:0,
                        credit_amount: balance_perido_a<0?(balance_perido_a*-1):0,
                        final_debit: final_balance_a>0?final_balance_a:0,
                        final_credit: final_balance_a<0?(final_balance_a*-1):0,
                    });
                    if (row.hasChildren) {// vuelve negrita los nodos padre
                            for(let col = 1; col <= 8; col++) {
                                newRow.getCell(col).font = { bold: true };
                            }
                        }

                    newRow.eachCell((cell, colNumber) => {
                            if (colNumber > 2) { // Ajusta este número según las columnas que deban tener el formato
                                cell.numFmt = accountingFormat;
                            }
                        });
                    });
                    

                    // Apply grouping based on level
                    flattenedData.forEach((row, index) => {
                    const currentRow = worksheet.getRow(index + 5); // +5 to account for header row
                    currentRow.outlineLevel = row.level;
                    });

                    // Set the worksheet to show outlines
                    worksheet.properties.outlineProperties = {
                        summaryBelow: false,
                        summaryRight: false,
                    };
                    let totals ={
                        initial_debit_amount: 0,
                        initial_credit_amount: 0,
                        debit_amount: 0,
                        credit_amount: 0,
                        final_debit: 0,
                        final_credit: 0
                    }
                    for(let item of obj[key]) {
                      let balance_inicial = parseFloat(item.initial_debit_amount) - parseFloat(item.initial_credit_amount);
                      let balance_perido = parseFloat(item.debit_amount)-parseFloat(item.credit_amount);
                      let final_balance_b = balance_perido + balance_inicial;

                        totals.initial_debit_amount+=balance_inicial>0?balance_inicial:0;
                        totals.initial_credit_amount+=balance_inicial<0?(balance_inicial*-1):0;
                        totals.debit_amount+=balance_perido>0?balance_perido:0;
                        totals.credit_amount+=balance_perido<0?(balance_perido*-1):0;
                        totals.final_debit+=final_balance_b>0?final_balance_b:0;
                        totals.final_credit+=final_balance_b<0?(final_balance_b*-1):0;

                    }
                    let row = worksheet.addRow(['','',totals.initial_debit_amount,totals.initial_credit_amount,totals.debit_amount,totals.credit_amount,totals.final_debit,totals.final_credit]);
                    row.eachCell((cell) => {
                        cell.numFmt = accountingFormat;
                        });
                        for(let i = 3; i <= 8; i++) { 
                        let cell = row.getCell(i);
                        cell.border = {
                            bottom: {style: 'double', color: {argb: 'black'}},
                        };
                        }
                    
                }
            }


      // Write the workbook to a file
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: "application/octet-stream" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `Balanza de comprobacion ${this.title} al ${this.filter_apply.dateEnd} .xlsx`;
      link.click();
    },
    async set_filters(res) {
      console.log(res);
      if (res.response) {
        this.loading_filter = true;
        await this.getData(res.filters);
      }
      this.loading_filter = false;
      this.show_filters = false;
      this.$root.$emit("toggle-mini", true);
    },
    formatDate(date) {
      return date ? moment(date).format('DD/MM/YYYY') : '';
    },
    ViewAccount(item) {
      const hasChildren = item.children && item.children.length > 0;
      const route = this.$router.resolve({
        name: "ViewEntryDiaryBook",
        params: { id: item.chart_of_account_id },
        query: {
          account_name: item.chart_of_account_name,
          account_code: item.chart_of_account_code,
          has_children: hasChildren ? '1' : '0',
          business: this.filter_apply.business_unit?.id || '',
          location: this.filter_apply.location?.id || 0,
          dateStart: this.filter_apply.dateStart || '',
          dateEnd: this.filter_apply.dateEnd || '',
        },
      });
      window.open(route.href, "_blank");
    },
   print_trial() {
    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.text("Balanza de Comprobación", 80, 10);
    doc.setFontSize(11);
    doc.text(this.print_title, 70, 15);
    doc.setFontSize(9);
    doc.text(`${moment().format('DD/MM/YYYY hh:mm')}`, 180, 10);

    const tableData = [];
    let grand_total_debit = 0;
    let grand_total_credit = 0;

    // Función recursiva para procesar los nodos y calcular los saldos
    const processNode = (node, father_expand, level = 0) => {
        if (level === 0 || this.expandedNodes.some(item => item.chart_of_account_code === node.chart_of_account_code) || father_expand) {
            const indent = ' '.repeat(level * 4);
            
            // Calc Net Balances (Same as UI)
            const bal_ini = parseFloat(node.initial_debit_amount) - parseFloat(node.initial_credit_amount);
            const bal_mov = parseFloat(node.debit_amount) - parseFloat(node.credit_amount);
            const bal_fin = bal_ini + bal_mov;

            tableData.push([
                `${indent}${node.chart_of_account_code} - ${node.chart_of_account_name}`,
                bal_ini > 0 ? this.currencyFormat(bal_ini) : "0.00",
                bal_ini < 0 ? this.currencyFormat(Math.abs(bal_ini)) : "0.00",
                bal_mov > 0 ? this.currencyFormat(bal_mov) : "0.00",
                bal_mov < 0 ? this.currencyFormat(Math.abs(bal_mov)) : "0.00",
                bal_fin > 0 ? this.currencyFormat(bal_fin) : "0.00",
                bal_fin < 0 ? this.currencyFormat(Math.abs(bal_fin)) : "0.00"
            ]);

            // Si el nodo tiene hijos y está expandido, procesar los hijos
            if (node.children && this.expandedNodes.some(item => item.chart_of_account_code === node.chart_of_account_code)) {
                node.children.forEach(child => {
                    processNode(child, true, level + 1);
                });
            }
        }
    };

    // Procesar los nodos del catálogo
    this.catalog_filtered.forEach(node => processNode(node, false));

    // Agregar la fila de totales con los nuevos cálculos
    tableData.push([
        "TOTALES:",
        this.currencyFormat(this.grand_totals.initial_debit_amount),
        this.currencyFormat(this.grand_totals.initial_credit_amount),
        this.currencyFormat(this.grand_totals.debit_amount),
        this.currencyFormat(this.grand_totals.credit_amount),
        this.currencyFormat(this.grand_totals.final_debit),
        this.currencyFormat(this.grand_totals.final_credit)
    ]);

    // Generar la tabla en el PDF
    doc.autoTable({
        head: [['Cuenta', 'Sal. Ini Deb', 'Sal. Ini Cre', 'Mov. Deb', 'Mov. Cre', 'Sal. Fin Deb', 'Sal. Fin Cre']],
        body: tableData,
        startY: 20,
        styles: { fontSize: 7 },
        columnStyles: {
            0: { cellWidth: 'auto' },
            1: { halign: 'right' },
            2: { halign: 'right' },
            3: { halign: 'right' },
            4: { halign: 'right' },
            5: { halign: 'right' },
            6: { halign: 'right' }
        }
    });

    // Descargar el PDF
    doc.save('Balanza_Comprobacion.pdf');
}
  },
};
</script>

<style scoped>
.trial-link {
  cursor: pointer;
  text-decoration: underline;
  text-decoration-style: dotted;
  text-underline-offset: 3px;
  transition: opacity 0.15s;
}
.trial-link:hover {
  opacity: 0.65;
}
.trial-tree .v-treeview-node__root {
  min-height: 44px;
  padding-right: 16px !important; /* Match px-4 padding */
}
.trial-tree .v-treeview-node__append {
  margin-left: auto;
}
.border-bottom {
  border-bottom: 1px solid #e0e0e0;
}
.w-100 {
  width: 100%;
}
</style>
